import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import MyWorksGrid from './MyWorksGrid';
import EditReviewModal from './EditReviewModal';
import EditEventModal from './EditEventModal';
import SavedItemDetail from './SavedItemDetail';

const API_URL = process.env.REACT_APP_API_URL;

// Map app language codes (TH/EN/CN) to grid label keys (th/en/zh)
const toGridLang = (lang) => {
  const v = (lang || 'th').toLowerCase();
  if (v === 'cn' || v === 'zh') return 'zh';
  if (v === 'en') return 'en';
  return 'th';
};

const getStoredUser = () => {
  try { return JSON.parse(localStorage.getItem('user') || 'null'); }
  catch { return null; }
};

/**
 * Self-contained "My Works" panel: fetches the current user's created
 * reviews / events / venues and renders them in the Instagram-style grid.
 * Designed to be shown as an in-app overlay (no route navigation to open it).
 */
const num = (x) => { const n = Number(x); return Number.isFinite(n) ? n : null; };
const hasCoords = (o) => o.latitude != null && o.longitude != null;

const MyWorksPanel = ({ language = 'th', refreshKey = 0, onMapItemsChange, view = 'grid' }) => {
  const navigate = useNavigate();
  const [userReviews, setUserReviews] = useState([]);
  const [userEvents, setUserEvents] = useState([]);
  const [userVenues, setUserVenues] = useState([]);
  const [editingReview, setEditingReview] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  // Item whose detail is shown inline inside this panel (null = show the grid)
  const [detailItem, setDetailItem] = useState(null);
  // Which category tab is selected in the grid (mirrored up from MyWorksGrid)
  const [activeType, setActiveType] = useState('reviews');

  const gridLang = toGridLang(language);

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  const fetchAll = useCallback(async () => {
    const user = getStoredUser();
    // Reviews
    axios.get(`${API_URL}/reviews/user`, authHeaders())
      .then((res) => setUserReviews(Array.isArray(res.data) ? res.data : []))
      .catch((err) => { console.error('[MyWorks] reviews error:', err); setUserReviews([]); });
    // Events (need creator id)
    if (user && user.id) {
      axios.get(`${API_URL}/events/creator/${user.id}`, authHeaders())
        .then((res) => setUserEvents(Array.isArray(res.data) ? res.data : []))
        .catch((err) => { console.error('[MyWorks] events error:', err); setUserEvents([]); });
    }
    // Venues / businesses
    axios.get(`${API_URL}/businesses/my`, authHeaders())
      .then((res) => setUserVenues(Array.isArray(res.data) ? res.data : []))
      .catch((err) => { console.error('[MyWorks] venues error:', err); setUserVenues([]); });
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll, refreshKey]);

  // Delete handlers
  const deleteReview = useCallback(async (id) => {
    try {
      await axios.delete(`${API_URL}/reviews/${id}`, authHeaders());
      setUserReviews((prev) => prev.filter((r) => r.id !== id));
    } catch (err) { console.error('[MyWorks] delete review error:', err); }
  }, []);

  const deleteEvent = useCallback(async (id) => {
    try {
      await axios.delete(`${API_URL}/events/${id}`, authHeaders());
      setUserEvents((prev) => prev.filter((e) => e.id !== id));
    } catch (err) { console.error('[MyWorks] delete event error:', err); }
  }, []);

  // Unified grid handlers
  // Tapping an event or review shows its detail inline in this panel (no route
  // navigation), matching the ที่บันทึกไว้ grid. Venues here are the user's
  // business record, so they still open the venue dashboard to manage it.
  const handleOpen = useCallback((type, item) => {
    if (type === 'venue') { navigate('/venue/dashboard'); return; }
    setDetailItem({ ...item, _source: type });
  }, [navigate]);

  const handleEdit = useCallback((type, item) => {
    if (type === 'event') setEditingEvent(item);
    else if (type === 'venue') navigate('/venue/dashboard');
    else setEditingReview(item);
  }, [navigate]);

  const handleDelete = useCallback((type, item) => {
    if (type === 'event') deleteEvent(item.id);
    else if (type === 'review') deleteReview(item.id);
  }, [deleteEvent, deleteReview]);

  // ── Map markers for the selected category ──────────────────────────────
  // Reshape each category's rows into the place shape the map overlay reads.
  const reviewPlaces = useMemo(() => userReviews.map((r) => ({
    id: `review_${r.id}`, event_id: `review_${r.id}`, _source: 'review',
    event_name: r.place_name_th || r.place_name_en || r.place_name || 'Review',
    name: r.place_name_th || r.place_name_en || r.place_name || '',
    place_name: r.place_name_th || r.place_name_en || r.place_name || '',
    image_url: r.main_image_url || r.thumbnail_url || null,
    event_image_link: r.main_image_url || r.thumbnail_url || null,
    latitude: num(r.latitude), longitude: num(r.longitude),
  })).filter(hasCoords), [userReviews]);

  const eventPlaces = useMemo(() => userEvents.map((e) => ({
    id: e.id, event_id: e.id, _source: 'event',
    event_name: e.event_name || 'Event',
    name: e.event_name || '',
    image_url: e.event_image_link || null,
    event_image_link: e.event_image_link || null,
    latitude: num(e.latitude), longitude: num(e.longitude),
  })).filter(hasCoords), [userEvents]);

  const venuePlaces = useMemo(() => userVenues.map((v) => ({
    id: `venue_${v.id}`, event_id: `venue_${v.id}`, _source: 'venue', main_category: 'Venue',
    event_name: v.venue_name || v.name || v.business_name || 'Venue',
    name: v.venue_name || v.name || v.business_name || '',
    image_url: v.logo_url || v.generated_thumbnail_url || v.image_url || v.main_image_url || null,
    event_image_link: v.logo_url || v.generated_thumbnail_url || v.image_url || v.main_image_url || null,
    latitude: num(v.latitude), longitude: num(v.longitude),
  })).filter(hasCoords), [userVenues]);

  const mapPlaces = useMemo(() => {
    if (activeType === 'events') return eventPlaces;
    if (activeType === 'venues') return venuePlaces;
    if (activeType === 'reviews') return reviewPlaces;
    return [];
  }, [activeType, reviewPlaces, eventPlaces, venuePlaces]);

  useEffect(() => {
    if (onMapItemsChange) onMapItemsChange(mapPlaces, `myworks-${activeType}`);
  }, [mapPlaces, activeType, onMapItemsChange]);

  return (
    <>
      {detailItem && view !== 'map' ? (
        <SavedItemDetail
          item={detailItem}
          language={language}
          onBack={() => setDetailItem(null)}
        />
      ) : (
        <MyWorksGrid
          reviews={userReviews}
          events={userEvents}
          venues={userVenues}
          language={gridLang}
          onOpen={handleOpen}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onActiveTypeChange={setActiveType}
          view={view}
        />
      )}

      {editingReview && (
        <EditReviewModal
          open
          review={editingReview}
          onClose={() => setEditingReview(null)}
          onSaved={(updated) => {
            setUserReviews((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
            setEditingReview(null);
          }}
          language={gridLang}
        />
      )}

      {editingEvent && (
        <EditEventModal
          open
          event={editingEvent}
          onClose={() => setEditingEvent(null)}
          onSaved={(updated) => {
            setUserEvents((prev) => prev.map((e) => (e.id === updated.id ? { ...e, ...updated } : e)));
            setEditingEvent(null);
          }}
          language={gridLang}
        />
      )}
    </>
  );
};

export default MyWorksPanel;
